import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ShieldAlert, ShieldCheck } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { api, fmtUSD } from "@/lib/api";

function Card({ id, icon: Icon, title, to, source, asOf, children }) {
  const nav = useNavigate();
  return (
    <button data-testid={`dept-dash-${id}`} onClick={() => nav(to)} className="panel panel-hover text-left flex flex-col overflow-hidden">
      <div className="px-5 pt-4 flex items-center gap-2 text-sm font-semibold text-slate-900"><span className="icon-chip w-7 h-7 rounded-lg"><Icon className="w-3.5 h-3.5" /></span>{title}</div>
      <div className="px-5 py-3 flex-1">{children}</div>
      <div className="px-5 py-2 border-t border-slate-100 text-[10px] num uppercase tracking-wider text-slate-400">{source} · as of {new Date(asOf).toLocaleDateString("en-US")}</div>
    </button>
  );
}

const Stat = ({ id, label, value, tone = "text-slate-900" }) => (
  <div><div className={`num text-2xl font-semibold ${tone}`} data-testid={`dept-stat-${id}`}>{value}</div><div className="text-[11px] text-slate-500">{label}</div></div>
);

export function DeptDashboards() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboards/departments").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const sev = d.risk.by_severity;
  return (
    <div data-testid="dept-dashboards">
      <div className="flex items-end justify-between mb-3"><h2 className="text-base md:text-lg font-semibold text-slate-900">Department dashboards</h2><span className="text-[11px] text-slate-500">Read-only views from connected systems</span></div>
      <div className="grid md:grid-cols-3 gap-3">
        <Card id="risk" icon={ShieldCheck} title="Risk issue status" to="/grc" source={d.risk.source} asOf={d.as_of}>
          <div className="flex gap-6"><Stat id="risk-open" label="Open issues" value={d.risk.open} /><Stat id="risk-overdue" label="Overdue" value={d.risk.overdue} tone="text-red-600" /></div>
          <div className="mt-3 space-y-1.5">
            {["High", "Medium", "Low"].map((s) => (
              <div key={s} className="flex items-center gap-2 text-[11px]"><span className="w-14 text-slate-600">{s}</span>
                <div className="flex-1 h-1.5 rounded bg-slate-100 overflow-hidden"><div className={`h-full ${s === "High" ? "bg-red-500" : s === "Medium" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${((sev[s] || 0) / Math.max(d.risk.open, 1)) * 100}%` }} /></div>
                <span className="num w-4 text-right text-slate-700">{sev[s] || 0}</span></div>
            ))}
          </div>
        </Card>
        <Card id="ofac" icon={ShieldAlert} title="OFAC name-screening matches" to="/task/task-kyc" source={d.ofac.source} asOf={d.as_of}>
          <div className="flex gap-6"><Stat id="ofac-pending" label="Pending review" value={d.ofac.pending} tone="text-amber-600" /><Stat id="ofac-cleared" label="False positives" value={d.ofac.false_positive} /><Stat id="ofac-true" label="True matches" value={d.ofac.true_match} tone="text-red-600" /></div>
          <div className="mt-3 space-y-1">
            {d.ofac.top_pending.slice(0, 3).map((m) => <div key={m.case_id + m.screened_name} className="flex justify-between text-[11px] gap-2"><span className="truncate text-slate-700">{m.screened_name} <span className="text-slate-400">vs</span> {m.matched_name}</span><span className="num text-slate-500 shrink-0">{m.score}%</span></div>)}
          </div>
        </Card>
        <Card id="wires" icon={Activity} title="Daily wire volume & value" to="/task/task-wire-ops" source={d.wires.source} asOf={d.as_of}>
          <div className="flex gap-6"><Stat id="wires-count" label="Wires · last 24h" value={d.wires.today.count} /><Stat id="wires-value" label="Value · last 24h" value={fmtUSD(d.wires.today.value, true)} /><Stat id="wires-banks" label="Banks" value={d.wires.today.banks} /></div>
          <div className="h-16 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.wires.days}><XAxis dataKey="date" hide /><Tooltip formatter={(v) => fmtUSD(v, true)} labelFormatter={(l) => new Date(`${l}T12:00:00`).toLocaleDateString("en-US")} /><Bar dataKey="value" fill="#0ea5e9" radius={[3, 3, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
